import React, { useEffect, useState, useCallback } from "react";
import { Conversation, getConversations } from "./utils/conversations";
import { ChatItem } from "react-chat-elements";
import { FiSearch } from "react-icons/fi";
import "react-chat-elements/dist/main.css";
import "tailwindcss/tailwind.css";
import ChatContainer from "./ChatContainer";
import Logo from "assets/img/LOGO.png";
import { PlusCircleIcon } from "@heroicons/react/24/solid";
import { getMessages } from "./utils/messageView";
import { markMessagesRead } from "server/Conversations CRUD/Requests";
import useAuthUser from "react-auth-kit/hooks/useAuthUser";
import { useNavigate } from "react-router-dom";

// Style CSS personnalisé pour forcer la couleur des badges
const customStyles = `
  .rce-citem__unread {
    background-color: #131CC9 !important;
    color: white !important;
  }
`;

const defaultAvatarUrl = "assets/img/logoJaune.png";

interface MessagesProps {
  userRole?: string;
  conversationType?: string;
}

const Messages: React.FC<MessagesProps> = ({
  userRole = "student",
  conversationType = "ProblemConversation",
}) => {
  const [conversations, setConversations] = useState<Conversation[]>([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [converSmView, setconverSmView] = useState<string>("");
  const [conversationId, setConversationId] = useState<string | null>(null);
  const [conversationTitle, setConversationTitle] = useState<string>("");
  const [chatSmView, setchatSmView] = useState<string>("hidden sm:block");
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [messages, setMessages] = useState<any[]>([]);
  const [showDefaultPage, setShowDefaultPage] = useState<boolean>(true);
  const currentUser = useAuthUser<any>();
  const navigate = useNavigate();

  // Fonction pour charger les conversations
  const fetchConversations = useCallback(
    async (showLoader = true) => {
      try {
        if (showLoader) {
          setIsLoading(true);
        }
        const convs = await getConversations(conversationType);
        setConversations(convs);

        // Si il y a des conversations, masquer la page par défaut
        if (convs.length > 0) {
          setShowDefaultPage(false);
        }
      } catch (error) {
        console.error("Erreur lors du chargement des conversations:", error);
      } finally {
        if (showLoader) {
          setIsLoading(false);
        }
      }
    },
    [conversationType],
  );

  useEffect(() => {
    fetchConversations();
  }, [fetchConversations]);

  // Rafraîchir les conversations périodiquement pour mettre à jour les messages non lus
  useEffect(() => {
    const interval = setInterval(() => {
      fetchConversations(false); // Pas de loader pour le rafraîchissement automatique
    }, 30000); // Rafraîchir toutes les 30 secondes

    return () => clearInterval(interval);
  }, [fetchConversations]);

  // Fonction pour marquer les messages comme lus
  const handleMarkMessagesRead = useCallback(
    async (conversationId: string) => {
      try {
        // Mise à jour optimiste de l'interface
        setConversations((prev) =>
          prev.map((conv) =>
            conv._id === conversationId
              ? { ...conv, unreadMessagesCount: 0 }
              : conv,
          ),
        );

        // Appel à l'API pour marquer comme lus
        const response = await markMessagesRead({ conversationId });

        if (response.status !== 200) {
          console.error(
            "Erreur lors du marquage des messages comme lus:",
            response.body,
          );
          // En cas d'erreur, recharger les conversations pour avoir l'état correct
          fetchConversations(false); // Pas de loader pour le rechargement d'erreur
        }
      } catch (error) {
        console.error("Erreur lors du marquage des messages comme lus:", error);
        // En cas d'erreur, recharger les conversations pour avoir l'état correct
        fetchConversations(false); // Pas de loader pour le rechargement d'erreur
      }
    },
    [fetchConversations],
  );

  // Fonction pour gérer le clic sur une conversation
  const handleConversationClick = useCallback(
    async (conversation: Conversation) => {
      setConversationTitle(conversation.title);
      setConversationId(conversation._id);
      setconverSmView("hidden sm:flex");
      setchatSmView("w-screen sm:w-full");

      // Charger les messages de la conversation
      try {
        const messagesData = await getMessages(
          conversation._id,
          currentUser?._id,
        );
        setMessages(messagesData);
      } catch (error) {
        console.error("Erreur lors du chargement des messages:", error);
      }

      // Marquer les messages comme lus si il y en a
      if (
        conversation.unreadMessagesCount &&
        conversation.unreadMessagesCount > 0
      ) {
        handleMarkMessagesRead(conversation._id);
      }
    },
    [handleMarkMessagesRead, currentUser?._id],
  );

  // Fonction pour gérer l'envoi d'un message
  const handleSendMessage = useCallback(
    async (messageText: string) => {
      // Ajouter le message optimistiquement à l'interface
      const newMessage = {
        id: `temp-${Date.now()}`,
        title: "Vous",
        position: "right" as const,
        type: "text" as const,
        text: messageText,
        date: new Date(),
        data: undefined,
      };

      setMessages((prev) => [...prev, newMessage]);

      // Rafraîchir les messages après un délai pour s'assurer qu'ils sont bien sauvegardés
      setTimeout(async () => {
        if (conversationId) {
          try {
            const messagesData = await getMessages(
              conversationId,
              currentUser?._id,
            );
            setMessages(messagesData);
          } catch (error) {
            console.error(
              "Erreur lors du rafraîchissement des messages:",
              error,
            );
          }
        }
      }, 1000);
    },
    [conversationId, currentUser?._id],
  );

  // Fonction pour gérer la fermeture du popup
  const handleClosePopup = useCallback(() => {
    // Cette fonction peut être utilisée pour gérer des popups spécifiques aux messages
    console.log("Popup fermé");
  }, []);

  // Filtrer les conversations basées sur la recherche
  const filteredConversations = conversations.filter((conversation) =>
    conversation.title.toLowerCase().includes(searchQuery.toLowerCase()),
  );

  // Fonction pour rediriger selon le rôle
  const handleRedirectByRole = () => {
    if (userRole === "student") {
      // Rediriger vers les soumissions pour les étudiants
      navigate("/submissions");
    } else if (userRole === "teacher") {
      // Rediriger vers les soumissions pour les enseignants
      navigate("/submissions");
    } else if (userRole === "parent") {
      // Rediriger vers les soumissions pour les parents
      navigate("/submissions");
    }
  };

  const handleBackToConversations = () => {
    setconverSmView("");
    setchatSmView("hidden sm:flex");
    setConversationId(null);
    setConversationTitle("");
    setMessages([]);
  };

  return (
    <div className="h-[650px] w-full">
      <style>{customStyles}</style>

      {/* Page par défaut - affichée quand il n'y a pas de conversations */}
      {showDefaultPage && !isLoading && conversations.length === 0 ? (
        <div className="h-full bg-white rounded-lg shadow-sm flex items-center justify-center">
          <div className="text-center">
            <div className="w-32 h-32 mx-auto mb-6">
              <img
                src={Logo}
                alt="Logo"
                className="w-full h-full object-contain"
              />
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">
              Messages d'exercices
            </h2>
            <p className="text-gray-600 mb-8 max-w-md mx-auto">
              Vous n'avez pas encore de conversations d'exercices.{" "}
              {userRole === "student"
                ? "Créez une soumission pour commencer à recevoir de l'aide."
                : "Consultez les soumissions pour voir les exercices."}
            </p>
            <button
              onClick={handleRedirectByRole}
              className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors flex items-center gap-2 mx-auto"
            >
              {userRole === "student" ? (
                <>
                  <PlusCircleIcon className="w-5 h-5" />
                  Créer une soumission
                </>
              ) : (
                <>
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"
                    />
                  </svg>
                  Voir les soumissions
                </>
              )}
            </button>
          </div>
        </div>
      ) : (
        <div className="flex h-full bg-white rounded-lg shadow-sm">
          {/* Sidebar des conversations */}
          <div
            className={`w-full sm:w-80 border-r border-gray-200 flex flex-col h-full max-h-full ${converSmView}`}
          >
            {/* Header */}
            <div className="p-4 border-b border-gray-200">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-lg font-semibold text-gray-800">
                  Messages
                </h2>
              </div>

              {/* Barre de recherche */}
              <div className="relative">
                <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  placeholder="Rechercher une conversation..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Liste des conversations */}
            <div className="flex-1 overflow-y-auto min-h-0">
              {isLoading ? (
                <div className="flex items-center justify-center h-full">
                  <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                </div>
              ) : filteredConversations.length === 0 ? (
                <div className="flex flex-col items-center justify-center h-full text-gray-500">
                  <p className="text-center">
                    {searchQuery
                      ? "Aucune conversation trouvée"
                      : "Aucune conversation"}
                  </p>
                </div>
              ) : (
                filteredConversations.map((conversation) => (
                  <ChatItem
                    key={conversation._id}
                    id={conversation._id}
                    avatar={Logo}
                    alt={conversation.title}
                    title={conversation.title}
                    subtitle={conversation.lastMessage?.body || "Aucun message"}
                    date={new Date(conversation.updatedAt)}
                    unread={conversation.unreadMessagesCount || 0}
                    onClick={() => handleConversationClick(conversation)}
                    className="cursor-pointer hover:bg-gray-50 transition-colors [&_.rce-citem__unread]:!bg-[#131CC9] [&_.rce-citem__unread]:!text-white [&_.rce-citem__unread]:!important"
                  />
                ))
              )}
            </div>
          </div>

          {/* Zone de chat */}
          <div className={`flex-1 flex flex-col h-full ${chatSmView}`}>
            {conversationId ? (
              <ChatContainer
                messages={messages}
                onSendMessage={handleSendMessage}
                onHandleClosePopup={handleClosePopup}
                conversationId={conversationId}
                supervisorId={
                  conversations.find((c) => c._id === conversationId)
                    ?.supervisorId
                }
                correctorId={
                  conversations.find((c) => c._id === conversationId)
                    ?.correctorId
                }
                isActivated={
                  conversations.find((c) => c._id === conversationId)
                    ?.isActivated
                }
                userId={currentUser?._id}
                conversationTitle={conversationTitle}
                onBack={handleBackToConversations}
                userRole={userRole}
              />
            ) : (
              <div className="flex items-center justify-center h-full">
                <div className="text-center text-gray-500">
                  <div className="w-32 h-32 mx-auto mb-4">
                    <img
                      src={Logo}
                      alt="Logo"
                      className="w-full h-full object-contain"
                    />
                  </div>
                  <h3 className="text-lg font-semibold mb-2">
                    Sélectionnez une conversation
                  </h3>
                  <p className="text-sm">
                    Choisissez une conversation dans la liste pour commencer à
                    discuter
                  </p>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Messages;
